import { createTheme, responsiveFontSizes, ThemeOptions } from '@mui/material';

type customColorObject = {
  gray0: string;
  gray1: string;
  gray2: string;
  gray3: string;
  gray4: string;
  gray5: string;
  gray6: string;
  gray7: string;
  gray8: string;
  gray9: string;
  gray10: string;
  gray11: string;
  gray12: string;
  gray13: string;
  successGreen: string;
  errorRed: string;
  focusBlue: string;
  darkGreen: string;
  orange5: string;
  [key: string]: string;
};

declare module '@mui/material/styles' {
  interface Theme {
    ragyaPalette: customColorObject;
  }

  interface ThemeOptions {
    ragyaPalette: customColorObject;
  }

  interface TypographyVariants {
    browseTitle?: React.CSSProperties;
    titleHuge?: React.CSSProperties;
    titleBig?: React.CSSProperties;
    titleBold?: React.CSSProperties;
    titleUppercaseBig?: React.CSSProperties;
    titleLight?: React.CSSProperties;
    titleSectionUppercase?: React.CSSProperties;
    bodyRegular?: React.CSSProperties;
    bodySemibold?: React.CSSProperties;
    bodyBold?: React.CSSProperties;
    small?: React.CSSProperties;
    smallSemiBold?: React.CSSProperties;
    smallBold?: React.CSSProperties;
    smallItalic?: React.CSSProperties;
    tiny?: React.CSSProperties;
    tinySemibold?: React.CSSProperties;
    tinyBold?: React.CSSProperties;
    mobile?: React.CSSProperties;
    mobileSemibold?: React.CSSProperties;
    mobileBold?: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    browseTitle?: React.CSSProperties;
    titleHuge?: React.CSSProperties;
    titleBig?: React.CSSProperties;
    titleBold?: React.CSSProperties;
    titleUppercaseBig?: React.CSSProperties;
    titleLight?: React.CSSProperties;
    titleSectionUppercase?: React.CSSProperties;
    bodyRegular?: React.CSSProperties;
    bodySemibold?: React.CSSProperties;
    bodyBold?: React.CSSProperties;
    small?: React.CSSProperties;
    smallSemiBold?: React.CSSProperties;
    smallBold?: React.CSSProperties;
    smallItalic?: React.CSSProperties;
    tiny?: React.CSSProperties;
    tinySemibold?: React.CSSProperties;
    tinyBold?: React.CSSProperties;
    mobile?: React.CSSProperties;
    mobileSemibold?: React.CSSProperties;
    mobileBold?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    browseTitle: true;
    titleHuge: true;
    titleBig: true;
    titleBold: true;
    titleUppercaseBig: true;
    titleLight: true;
    titleSectionUppercase: true;
    bodyRegular: true;
    bodySemibold: true;
    bodyBold: true;
    small: true;
    smallSemiBold: true;
    smallBold: true;
    smallItalic: true;
    tiny: true;
    tinySemibold: true;
    tinyBold: true;
    mobile: true;
    mobileSemibold: true;
    mobileBold: true;
  }
}

const ragyaOrange = '#FF7241';
const gray = '#0C1010';
const neutralGray = '#779696';
const defaultTextGray = '#D3E4E4';

export const ragyaGray = 'rgba(255, 255, 255, 0.07)';

const baseThemeOptions: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 500,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  ragyaPalette: {
    gray0: '#E9F3F3',
    gray1: '#E9F3F3',
    gray2: '#BCD0D0',
    gray3: '#A7C0C0',
    gray4: '#8CACAC',
    gray5: '#779696',
    gray6: '#668080',
    gray7: '#5A7272',
    gray8: '#475B5B',
    gray9: '#3A4949',
    gray10: '#354040',
    gray11: '#2D3636',
    gray12: '#1E2323',
    gray13: '#000',
    successGreen: '#00B254',
    errorRed: '#FF3C3C',
    focusBlue: '#0E9AFF',
    darkGreen: '#124600',
    orange5: '#601900',
    ragyaOrange: '#FF7241',
    darkOrange: '#2D150D',
  },
  palette: {
    mode: 'dark',

    primary: {
      main: ragyaOrange,
    },
  },
  components: {
    // the name of the component you're going to modify,
    // Autocomplete is your friend here, also look up the API
    // for the MUI classname you want to target
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          titleHuge: 'h1',
          titleBig: 'h3',
          titleBold: 'h2',
          titleUppercaseBig: 'h3',
          titleLight: 'h2',
          titleSectionUppercase: 'h3',
          bodyRegular: 'p',
          bodySemibold: 'p',
          bodyBold: 'p',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        // the name of the sub-class of the component that you want to define styling for
        containedPrimary: {
          borderRadius: 42,
          '&:hover': {
            backgroundColor: 'darken(#4FB13F, 5%)',
          },
          // width: 88,
          // height: 32,
          color: '#fff',
          variant: 'body2',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        label: {
          fontFamily: 'Amulya, serif',
          fontSize: '12px !important',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        thumb: {
          color: 'white',
        },
      },
    },
  },
  typography: {
    titleHuge: {
      fontSize: 42,
      fontWeight: 800,
      letterSpacing: 0,
      fontFamily: 'Amulya, serif',
      lineHeight: '44.1px',
    },
    titleBig: {
      fontSize: 32,
      fontWeight: 800,
      letterSpacing: 0,
      fontFamily: 'Amulya, serif',
      lineHeight: '40px',
    },
    titleBold: {
      fontSize: 22,
      fontWeight: 800,
      letterSpacing: 1,
      fontFamily: 'Amulya, serif',
      lineHeight: '24px',
    },
    titleUppercaseBig: {
      fontSize: 22,
      fontWeight: 100,
      letterSpacing: '3.85px',
      fontFamily: 'Amulya, serif',
      lineHeight: '40px',
    },
    titleLight: {},
    titleSectionUppercase: {},
    bodyRegular: {
      fontSize: 16,
      fontWeight: 400,
      letterSpacing: '0px',
      fontFamily: 'Amulya, serif',
      lineHeight: '24px',
    },
    bodySemibold: {},
    bodyBold: {
      fontSize: 16,
      fontWeight: 800,
      letterSpacing: '0px',
      fontFamily: 'Amulya, serif',
      lineHeight: '24px',
    },
    small: {
      fontSize: 12,
      fontWeight: 400,
      letterSpacing: '0px',
      fontFamily: 'Amulya, serif',
      lineHeight: '12px',
    },
    smallSemiBold: {},
    smallBold: {
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: '0px',
      fontFamily: 'Amulya, serif',
      lineHeight: '14px',
    },
    smallItalic: {},
    tiny: {},
    tinySemibold: {},
    tinyBold: {},
    mobile: {},
    mobileSemibold: {},
    mobileBold: {},
    h1: {
      fontSize: 42,
      fontFamily: 'Amulya,serif',
      fontWeight: 700,
      // fontStyle: 'italic',
    },
    h2: {
      fontSize: 16,
      fontWeight: 700,
      fontFamily: 'Amulya, serif',
    },
    h3: {
      fontSize: 16,
      fontWeight: 500,
      letterSpacing: 1,
      fontFamily: 'Amulya, serif',
    },
    h4: {
      fontSize: 12,
      fontWeight: 500,
      letterSpacing: 2,
      fontFamily: 'Amulya, serif',
      textTransform: 'none',
    },
    browseTitle: {
      fontSize: 28,
      fontWeight: 500,
      letterSpacing: 2,
      fontFamily: 'Amulya, serif',
    },
    body1: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '24px',
      fontFamily: 'Amulya, serif',
      letterSpacing: '0.5px',
    },
    body2: {
      fontSize: 14,
      fontWeight: 400,
      fontFamily: 'Amulya, serif',
      letterSpacing: '0.5px',
    },
    subtitle1: {
      fontSize: 12,
      fontFamily: 'Amulya, serif',
    },
    subtitle2: {
      fontSize: 10,
      fontFamily: 'Amulya, serif',
    },
    button: {
      fontSize: 16,
      fontFamily: 'Amulya, serif',
      fontWeight: 700,
    },
  },
};

const theme = responsiveFontSizes(createTheme(baseThemeOptions));

export const lightTheme = responsiveFontSizes(
  createTheme({
    ...baseThemeOptions,
    palette: {
      ...baseThemeOptions.palette,
      mode: 'light',
      // background: { paper: '#1E2323' },
      background: {
        paper: 'white',
      },
    },
    typography: {
      ...baseThemeOptions.typography,
      h1: {
        fontSize: 24,
        fontFamily: 'Amulya, sans-serif',
        letterSpacing: '-1px',
      },
    },
  })
);

export default theme;
